import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { useRemirrorContext } from "@remirror/react";
import overlaySlice, {
	closeOverlay,
	openOverlay,
} from "../../../../../layouts/Overlay/overlaySlice";
import { getImagesAroundPosition } from "../../utils/getImagesAroundPosition";
import { Node, NodeType } from "@remirror/pm/dist-types/model";
import VideoSlider from "./VideoSlider";
import { globalSaver } from "../../../../../network/saver";
import { selectGuidePreview } from "../../../../../redux/slices/guideSlice";
import { Id } from "../../../../../core/types/baseTypes";
import { openModal } from "../../../../../layouts/CModal/modalSlice";
// Import other hooks if necessary

const useAddScreenshot = (
	type: "videoTranscript" | "textEditor" | "newVideoTranscript",
) => {
	const dispatch = useAppDispatch();
	const guidePreview = useAppSelector(selectGuidePreview);
	const { commands, manager } = useRemirrorContext();
	const src = useAppSelector(
		(state) => state.guide.value.guide?.guideData.video.originalSrc,
	) as string;
	// Define the command to insert the screenshot
	const insertScreenshot = async (uri: string, timestamp: number) => {
		const resp = await globalSaver.selectedRegionalSaver.uploadImage(
			//@ts-ignore
			guidePreview?.id,
			uri,
		);

		getAspectRatioFromDataURI(uri, (aspectRatio: number) => {
			let node: Node = manager.schema.nodes.image.create({
				src: uri,
				metadata: {
					stepId: Date.now().toString(),
					timestamp: timestamp,
					s3ObjectId: resp,
				},
				width: aspectRatio < 1 ? "240px" : "100%",
			});
			if (type === "textEditor") {
				node = manager.schema.nodes.paragraph.create(
					{ nodeTextAlignment: "center" },
					[node],
				);
			}
			commands.insertNode(node);
			dispatch(closeOverlay());
		});
	};
	// Retrieve original source from the current guide
	// Define original timestamps based on cursor position and existing screenshots

	const isTextEditor = type === "textEditor";

	const reloadSrc = async () => {
		const newData = await globalSaver.selectedRegionalSaver.guides.data.get(
			guidePreview?.id as Id,
		);
		return newData?.video.originalSrc as string;
	};
	// Pass to component

	return () => {
		const pos = manager.view.state.selection;
		const { start, end } = getImagesAroundPosition(
			pos.from,
			manager.view.state.doc,
		);
		dispatch(
			openOverlay({
				heading: {
					icon: null,
					title: isTextEditor ? "Add Screenshot" : "Add Sync Marker",
					data: "",
				},
				component: (
					<VideoSlider
						type={type}
						source={src}
						initialStartTimestamp={isTextEditor ? 0 : start}
						initialEndTimestamp={isTextEditor ? undefined : end}
						addImage={insertScreenshot}
						reloadSource={reloadSrc}
					/>
				),
			}),
		);
	};
};

function getAspectRatioFromDataURI(
	dataURI: string,
	callback: (aspectRatio: number) => void,
) {
	// Create an image object
	const img = new Image();

	// Define the onload function
	img.onload = () => {
		// Get the image dimensions
		const width = img.width;
		const height = img.height;

		// Calculate the aspect ratio
		const aspectRatio = width / height;

		// Callback with the aspect ratio
		callback(aspectRatio);
	};

	// Set the image source to the data URI
	img.src = dataURI;
}

export default useAddScreenshot;
